import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { ShortVideo } from '@/components/ShortVideo';
import { LevelPill } from '@/pages/ExcelCoursePage';
import {
  excelCourse, excelClasses, getExcelClass, getModule, classPath, moduleLabel, moduleAnchor, lengthLabel, isoDuration,
  sourceLabel, SITE,
} from '@/content/excelCourse';

/**
 * /excel-course/class-<N>/ — one Excel Study class: the vertical player, the
 * AI prompt to try (with a copy button), the answer that was checked in
 * Excel, Microsoft sources, and prev/next. One template for every class.
 */
export function ExcelClassPage() {
  const { classSlug } = useParams<{ classSlug: string }>();
  const n = Number(classSlug?.match(/^class-(\d+)$/)?.[1]);
  const c = Number.isFinite(n) ? getExcelClass(n) : undefined;
  // Keyed by video so prev/next (same component instance) starts on the poster.
  const [playingId, setPlayingId] = useState<string | null>(null);
  if (!c) return <Navigate to="/excel-course/" replace />;

  const i = excelClasses.indexOf(c);
  const prev = excelClasses[i - 1];
  const next = excelClasses[i + 1];
  const url = `${SITE}${classPath(c.class)}`;
  const mod = getModule(c.module);
  const name = `Excel Class ${c.class}: ${c.topic}`;

  return (
    <div>
      <SEO
        title={`${c.topic} — Excel Class ${c.class} | Free Excel course | ACES`}
        description={`${c.description} A free ${lengthLabel(c.length_seconds)} video class from ${excelCourse.name}, with an AI prompt to try and the answer checked in Excel.`}
        canonical={url}
        ogImage={`${SITE}${c.thumbnail}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'VideoObject',
              name,
              description: c.description,
              thumbnailUrl: [`${SITE}${c.thumbnail}`],
              ...(c.upload_date ? { uploadDate: c.upload_date } : {}),
              duration: isoDuration(c.length_seconds),
              embedUrl: `https://www.youtube-nocookie.com/embed/${c.video_id}`,
              url,
              inLanguage: 'en',
              isAccessibleForFree: true,
              publisher: { '@type': 'Organization', name: excelCourse.by },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
                { '@type': 'ListItem', position: 2, name: 'Excel course', item: `${SITE}/excel-course/` },
                { '@type': 'ListItem', position: 3, name: `Class ${c.class}`, item: url },
              ],
            },
          ],
        }}
      />

      {/* Breadcrumb */}
      <div className="bg-cream/70 border-b border-ink-200/80">
        <nav className="max-w-5xl mx-auto px-4 py-3 text-xs text-ink-500 flex flex-wrap items-center gap-1.5">
          <Link to="/" className="hover:text-accent-600">Home</Link><span className="text-ink-300">·</span>
          <Link to="/excel-course/" className="hover:text-accent-600">Excel course</Link><span className="text-ink-300">·</span>
          <Link to={mod ? `/excel-course/#${moduleAnchor(mod)}` : '/excel-course/'} className="hover:text-accent-600">{c.module}</Link><span className="text-ink-300">·</span>
          <span className="text-ink-700 font-medium">Class {c.class}</span>
        </nav>
      </div>

      <section className="aces-hero">
        {/* Phone: title, player, details. Desktop: player left, title + details right. */}
        <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12 grid gap-6 md:gap-x-10 md:gap-y-0 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)] md:grid-rows-[auto_1fr] md:items-start">
          {/* Title */}
          <div className="min-w-0 md:col-start-2 md:row-start-1">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600">
              {excelCourse.name} · {mod ? `${moduleLabel(mod)}: ` : ''}{c.module}
            </span>
            <h1 className="font-serif text-[2rem] sm:text-[2.6rem] text-ink-900 tracking-tight leading-[1.08] mt-2 text-balance">
              <span className="block text-ink-500 text-lg sm:text-xl font-sans font-medium tracking-normal mb-1">Class {c.class}</span>
              {c.topic}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <LevelPill level={c.level} />
              <span className="aces-pill">{lengthLabel(c.length_seconds)} video</span>
              <span className="aces-pill">Free</span>
            </div>
          </div>

          {/* Player */}
          <div className="w-full max-w-[300px] sm:max-w-[340px] mx-auto md:mx-0 md:col-start-1 md:row-start-1 md:row-span-2">
            <div className="rounded-card-lg overflow-hidden shadow-card-raised border border-ink-200/80">
              <ShortVideo
                videoId={c.video_id}
                title={name}
                poster={c.thumbnail}
                playing={playingId === c.video_id}
                onPlay={() => setPlayingId(c.video_id)}
                eager
              />
            </div>
            <a
              href={c.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-ink-600 hover:text-ink-900 underline-offset-4 hover:underline"
            >
              Watch on YouTube ↗
            </a>
          </div>

          {/* Details */}
          <div className="min-w-0 md:col-start-2 md:row-start-2">
            <p className="md:mt-5 text-lg text-ink-700 leading-relaxed">{c.description}</p>

            <AiPrompt prompt={c.ai_prompt} answer={c.ai_answer} />

            {c.sources.length > 0 && (
              <div className="mt-8">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2">Sources: Microsoft support</h2>
                <ul className="space-y-1.5 text-sm">
                  {c.sources.map((s) => (
                    <li key={s}>
                      <a href={s} target="_blank" rel="noopener noreferrer" className="text-acct-600 hover:text-acct-700 underline underline-offset-4 decoration-acct-200 break-words">
                        {sourceLabel(s)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Prev / next */}
      <nav className="bg-cream border-t border-ink-200" aria-label="More classes">
        <div className="max-w-5xl mx-auto px-4 py-8 grid gap-3 sm:grid-cols-2">
          {prev ? (
            <Link to={classPath(prev.class)} className="aces-card-hover rounded-card border border-ink-200/80 bg-paper p-4 shadow-card">
              <span className="text-xs text-ink-500">← Previous · Class {prev.class}</span>
              <span className="block font-serif text-lg text-ink-900 mt-0.5">{prev.topic}</span>
            </Link>
          ) : <span className="hidden sm:block" />}
          {next ? (
            <Link to={classPath(next.class)} className="aces-card-hover rounded-card border border-ink-200/80 bg-paper p-4 shadow-card sm:text-right">
              <span className="text-xs text-ink-500">Next · Class {next.class} →</span>
              <span className="block font-serif text-lg text-ink-900 mt-0.5">{next.topic}</span>
            </Link>
          ) : (
            <Link to="/excel-course/#coming-next" className="aces-card-hover rounded-card border border-ink-200/80 bg-paper p-4 shadow-card sm:text-right">
              <span className="text-xs text-ink-500">Up next</span>
              <span className="block font-serif text-lg text-ink-900 mt-0.5">More classes coming — see the plan →</span>
            </Link>
          )}
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-10 text-center">
          <Link to="/excel-course/" className="text-sm font-medium text-accent-700 hover:text-accent-600 underline-offset-4 hover:underline">
            All {excelClasses.length} classes in the Excel course
          </Link>
        </div>
      </nav>
    </div>
  );
}

function AiPrompt({ prompt, answer }: { prompt: string; answer: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the text is still selectable */ }
  }

  return (
    <div className="mt-8 rounded-card border border-ink-200/80 bg-cream shadow-card overflow-hidden">
      <div className="px-5 pt-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif text-xl text-ink-900">Try this AI prompt</h2>
          <button
            onClick={copy}
            className="shrink-0 rounded-lg border border-ink-200 bg-paper px-3 py-1.5 text-xs font-medium text-ink-700 hover:border-ink-300 hover:bg-white transition-colors duration-150"
          >
            {copied ? 'Copied ✓' : 'Copy prompt'}
          </button>
        </div>
        <p className="mt-1 text-xs text-ink-500">Works in ChatGPT, Gemini, Copilot or Claude.</p>
        <blockquote className="mt-3 rounded-lg bg-paper border border-ink-200/70 px-4 py-3 text-[0.95rem] text-ink-800 leading-relaxed select-all">
          {prompt}
        </blockquote>
      </div>
      {answer && (
        <div className="mt-5 px-5 py-4 bg-econ-50 border-t border-econ-100">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-econ-600">The answer that works</h3>
            <span className="rounded-full border border-econ-200 bg-white/70 px-2 py-0.5 text-[11px] font-medium text-econ-600">✓ checked in Excel</span>
          </div>
          <pre className={`mt-2 whitespace-pre-wrap break-words text-ink-900 leading-relaxed ${answer.startsWith('=') ? 'font-mono text-sm' : 'font-sans text-[0.95rem]'}`}>{answer}</pre>
        </div>
      )}
      <p className="px-5 py-3 text-xs text-ink-500 border-t border-ink-200/70">
        AI can get Excel wrong. Paste its answer into Excel and test it before you trust it.
      </p>
    </div>
  );
}
